// hd-redesign-p1 — the at-home kit scrub.
//
// From the export's setKit/scrubAt/scrubDown, values carried verbatim:
//   · bar height, knob top and label top are all p x 100%
//   · the label reads Math.round(p * 70) + '%'  — 70, not 100. The control
//     shows how far through the PROTOCOL you are, and the copy claims results
//     improve by up to 70%; the number is that claim, not the drag position.
//   · layer 2 ramps over p 0.30 -> 0.42, layer 3 over 0.66 -> 0.78
//   · the hint fades once p > 0.04, and never comes back
//   · the knob scales 1.12 while grabbing (CSS, via .is-grabbing)
//
// There is NO autoplay here, by design: the control is the reader's, and it is
// the one piece of motion on the page that never moves on its own. That makes
// it correct under reduced motion with no branch at all.
//
// Pointer capture is taken on the rail so a drag that leaves the element keeps
// tracking, and window-level move/up listeners catch a release outside it.
//
// ── THE CONTROL TURNS BELOW 960, AND THAT IS THE WHOLE TOUCH STORY ──────────
// Vertical drag and vertical page scroll are the same gesture. The rail shipped
// 140x420 with `touch-action: none`, which resolved that conflict by taking the
// gesture always: a reader who put a thumb on the protocol could not scroll past
// it. Neither `none` nor `auto` is right — one traps the page, the other kills
// the control.
//
// So below 960 the rail is HORIZONTAL and `touch-action: pan-y` (assets/hd-p1
// .css). Now the two gestures are different axes and the browser can arbitrate
// without either side guessing: a vertical swipe always scrolls the page, a
// horizontal drag always works the control. It is the resolution
// assets/hd-sections.css:213 already ships for the before/after slider.
//
// Everything that follows is written per AXIS rather than per property, and the
// horizontal placement uses inset-inline-start, so Arabic fills from the right
// with no second code path. Above 960 the vertical control is untouched.
//
// ── AND IT TURNS BACK BELOW 720 (MOBILE DENSITY ROUND) ──────────────────────
// On a phone the control is vertical again, but SHORT and NARROW — a 56px strip
// beside ONE frame, not the 420px rail beside one — so the gesture conflict
// above does not come back: `touch-action: pan-x` (hd-p1.css) keeps the axis the
// control does not use, and a thumb aimed at the page lands beside the strip,
// not on it. The axis logic below is unchanged; only the question "which
// orientation is in force" gained a second term.
//
// THERE IS ONE CROSS-FADE MECHANISM AND THE PHONE USES IT. An earlier cut of
// this round laid the three layers out side by side as three frames and dimmed
// the unreached ones to a floor; that made a second mechanism, and the owner
// ruled it out. Below 720 the media is the SAME single frame box with the SAME
// stacked layers and the SAME two ramps, so the two opacity lines are
// unconditional and there is no phone branch left in this file at all. The
// cross-fade is driven by the drag itself with no CSS transition anywhere, so
// prefers-reduced-motion gets the instant swap it gets on the desktop.
//
// data-kit-step (1..3, the frame the reader is on, switched at each ramp's
// midpoint) drives the one caption under the frame in hd-p1.css and the
// slider's aria-valuetext below.
(function () {
  "use strict";

  function init(root) {
    var scrub = root.querySelector("[data-scrub]");
    var bar = root.querySelector("[data-scrub-bar]");
    var knob = root.querySelector("[data-scrub-knob]");
    var pct = root.querySelector("[data-scrub-pct]");
    // TWO hints, not one: the vertical rail says "slide down" and the
    // horizontal one cannot. Both are in the DOM, CSS shows one, and both fade.
    var hints = Array.prototype.slice.call(root.querySelectorAll("[data-scrub-hint]"));
    var l2 = root.querySelector("[data-scrub-layer='2']");
    var l3 = root.querySelector("[data-scrub-layer='3']");
    if (!scrub || !bar || !knob || !pct) return;

    // The breakpoints are read once and watched, not re-queried per frame.
    var HORIZ = window.matchMedia("(max-width: 960px)");
    var PHONE = window.matchMedia("(max-width: 720px)");
    var current = 0;

    // horizontal only in the band between the two: 721..960
    function horizontalNow() {
      return HORIZ.matches && !PHONE.matches;
    }

    function ramp(p, a, b) {
      return Math.max(0, Math.min(1, (p - a) / (b - a)));
    }

    function setKit(p) {
      p = Math.max(0, Math.min(1, p));
      current = p;
      var horizontal = horizontalNow();
      var pc = (p * 100).toFixed(2) + "%";

      // Each axis writes its own property and CLEARS the other's, so a rotation
      // never leaves a stale inline value behind to fight the stylesheet.
      if (horizontal) {
        bar.style.width = pc;
        bar.style.height = "";
        knob.style.insetInlineStart = pc;
        knob.style.top = "";
        pct.style.insetInlineStart = pc;
        pct.style.top = "";
      } else {
        bar.style.height = pc;
        bar.style.width = "";
        knob.style.top = pc;
        knob.style.insetInlineStart = "";
        pct.style.top = pc;
        pct.style.insetInlineStart = "";
      }

      pct.textContent = Math.round(p * 70) + "%";
      scrub.setAttribute("aria-valuenow", String(Math.round(p * 70)));
      // it was announced as horizontal while being vertical: role="slider"
      // defaults to horizontal and the markup declared nothing
      scrub.setAttribute("aria-orientation", horizontal ? "horizontal" : "vertical");
      if (l2) l2.style.opacity = String(ramp(p, 0.3, 0.42));
      if (l3) l3.style.opacity = String(ramp(p, 0.66, 0.78));

      // THE STEP, AND THE ONE PLACE ITS WORDS COME FROM. The thresholds are the
      // two ramps' midpoints, so the caption changes exactly when the frame the
      // reader is looking at does. aria-valuetext is read off the caption span
      // rather than assembled here: the sentence is authored in locales/ in both
      // languages, and a slider that announced a bare 0..70 said nothing about
      // which of the three steps that was.
      var step = p >= 0.72 ? "3" : p >= 0.36 ? "2" : "1";
      root.setAttribute("data-kit-step", step);
      var stepEl = root.querySelector(".hd-p1-kit__step--" + step);
      if (stepEl) {
        var stepText = (stepEl.textContent || "").trim();
        if (stepText) scrub.setAttribute("aria-valuetext", stepText);
      }
      if (p > 0.04) hints.forEach(function (h) { h.style.opacity = "0"; });
    }

    function at(e) {
      var r = scrub.getBoundingClientRect();
      if (!horizontalNow()) {
        setKit((e.clientY - r.top) / r.height);
        return;
      }
      // inline axis: the far end is the LEFT edge under rtl, so the fraction is
      // taken from the inline start rather than from x
      var rtl = window.getComputedStyle(scrub).direction === "rtl";
      var x = (e.clientX - r.left) / r.width;
      setKit(rtl ? 1 - x : x);
    }

    var onBreakpoint = function () { setKit(current); };
    [HORIZ, PHONE].forEach(function (mq) {
      if (mq.addEventListener) mq.addEventListener("change", onBreakpoint);
      else if (mq.addListener) mq.addListener(onBreakpoint);
    });

    scrub.addEventListener("pointerdown", function (e) {
      if (scrub.setPointerCapture) {
        try {
          scrub.setPointerCapture(e.pointerId);
        } catch (err) {
          /* a capture refused is not a reason to break the drag */
        }
      }
      scrub.classList.add("is-grabbing");
      at(e);

      function move(ev) {
        at(ev);
      }
      // pointercancel matters now that the browser is allowed to claim the
      // gesture: under touch-action: pan-y a vertical swipe that starts on the
      // rail becomes a page scroll and the pointer stream ENDS in a cancel, not
      // an up. Without this the control stayed latched in .is-grabbing.
      function up() {
        scrub.classList.remove("is-grabbing");
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
        window.removeEventListener("pointercancel", up);
      }
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
      window.addEventListener("pointercancel", up);
    });

    // Keyboard parity. The export has none — it is a mouse prototype — but a
    // control that carries the section's only state cannot be pointer-only.
    //
    // THE INLINE-AXIS KEYS FOLLOW READING DIRECTION. Up/Down are block-axis and
    // mean the same thing everywhere; Left/Right are not. ArrowRight increased
    // unconditionally here, which is backwards on the Arabic storefront and
    // contradicts the convention the theme already ships and already asserts —
    // tools/hd_dir_geometry.js measures ArrowRight as 50->45 under rtl and
    // 50->55 under ltr on the before/after slider. Same rule, same file, one
    // control later.
    scrub.addEventListener("keydown", function (e) {
      // read the state, not one axis's inline style, which is empty in the
      // orientation that is not currently in force
      var cur = current;
      var rtl = window.getComputedStyle(scrub).direction === "rtl";
      var fwd = 0.05;
      var d = 0;
      if (e.key === "ArrowDown") d = fwd;
      else if (e.key === "ArrowUp") d = -fwd;
      else if (e.key === "ArrowRight") d = rtl ? -fwd : fwd;
      else if (e.key === "ArrowLeft") d = rtl ? fwd : -fwd;
      else if (e.key === "Home") return e.preventDefault(), setKit(0);
      else if (e.key === "End") return e.preventDefault(), setKit(1);
      else return;
      e.preventDefault();
      setKit(cur + d);
    });

    setKit(0);
    root.__hdSetKit = setKit;
  }

  function boot(scope) {
    Array.prototype.slice
      .call((scope || document).querySelectorAll("[data-kit-root]"))
      .forEach(init);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      boot(document);
    });
  } else {
    boot(document);
  }

  document.addEventListener("shopify:section:load", function (e) {
    boot(e.target);
  });
})();
